"use client"

import { useState } from "react"
import Link from "next/link"
import { useAuth, ApiError } from "@/lib/auth"
import { api } from "@/lib/api"

export default function StudentLoginPage() {
  const { loginStudent } = useAuth()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    const u = username.trim()
    if (!u) {
      setError("請輸入學號")
      return
    }
    if (!password) {
      setError("請輸入密碼")
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await loginStudent(u, password)
      let dest = "/student/home"
      try {
        const token = localStorage.getItem("yuzhou.accessToken")
        const [pet, me] = await Promise.all([
          api.get<{ id: string } | null>("/api/pets/me", { token: token ?? undefined }),
          api.get<{ currentLevel: number | null }>("/api/users/me", { token: token ?? undefined }),
        ])
        if (!pet) dest = "/student/adopt"
        else if (me?.currentLevel == null) dest = "/student/placement"
        else dest = "/student/home"
      } catch {
        dest = "/student/adopt"
      }
      window.location.assign(dest)
      return
    } catch (err) {
      console.error("[student/login] failed", err)
      let msg: string
      if (err instanceof ApiError) {
        msg =
          err.status === 401
            ? "學號或密碼錯誤，請再試一次"
            : err.message || "登入失敗，請稍後再試"
      } else if (err instanceof TypeError) {
        msg = "無法連線到伺服器，請檢查網路後再試"
      } else {
        msg = "登入失敗，請稍後再試"
      }
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #E8F6FD 0%, #F0F8FF 40%, #FFF8E8 100%)" }}
    >
      <div className="absolute top-6 left-6 text-3xl opacity-50 animate-bounce">☁️</div>
      <div className="absolute top-10 right-10 text-2xl opacity-40">⭐</div>
      <div className="absolute bottom-32 right-8 text-3xl opacity-40">🎵</div>

      <div className="w-full max-w-sm space-y-6 text-center relative z-10">
        <div className="space-y-2">
          <div
            className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center text-4xl shadow-[0_8px_24px_rgba(77,184,232,0.35)]"
            style={{ background: "linear-gradient(135deg, #4DB8E8 0%, #2EA3D5 100%)" }}
          >
            🌏
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "#2D3748" }}>
            普通話學園
          </h1>
          <p className="text-sm" style={{ color: "#4DB8E8" }}>
            歡迎回來！
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          className="rounded-3xl p-6 space-y-4 text-left"
          style={{ background: "white", boxShadow: "0 4px 24px rgba(77,184,232,0.12)" }}
        >
          {error && (
            <p
              role="alert"
              className="text-sm rounded-xl px-4 py-3"
              style={{ background: "#FFE4E4", color: "#C53030" }}
            >
              {error}
            </p>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium" style={{ color: "#2D3748" }}>
              學號
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="例如：s001"
              autoComplete="username"
              className="w-full rounded-2xl border-2 px-4 py-3 outline-none"
              style={{
                borderColor: username ? "#4DB8E8" : "#E2E8F0",
                background: "#FAFBFF",
                color: "#2D3748",
              }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" style={{ color: "#2D3748" }}>
              密碼
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="輸入密碼"
              autoComplete="current-password"
              className="w-full rounded-2xl border-2 px-4 py-3 outline-none"
              style={{
                borderColor: password ? "#4DB8E8" : "#E2E8F0",
                background: "#FAFBFF",
                color: "#2D3748",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full py-4 text-lg font-bold text-white transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            style={{
              background: "linear-gradient(135deg, #4DB8E8 0%, #2EA3D5 100%)",
              boxShadow: "0 4px 16px rgba(77,184,232,0.4)",
            }}
          >
            {submitting ? "登入中..." : "登入 →"}
          </button>
        </form>

        <Link href="/" className="text-sm" style={{ color: "#718096" }}>
          ← 返回首頁
        </Link>
      </div>
    </main>
  )
}
